export const activeVisibilityPollMs = 30_000;
export const hiddenVisibilityPollMs = 2 * 60_000;

interface VisibilityPollingOptions {
  activeMs?: number;
  hiddenMs?: number;
  runImmediately?: boolean;
}

// Keep live pages current without making hidden tabs noisy. Focus and a return
// to the foreground always trigger an immediate refresh.
export function startVisibilityPolling(
  poll: () => void | Promise<void>,
  options: VisibilityPollingOptions = {},
): () => void {
  const activeMs = options.activeMs ?? activeVisibilityPollMs;
  const hiddenMs = options.hiddenMs ?? hiddenVisibilityPollMs;
  let stopped = false;
  let running = false;
  let lastRunAt = 0;
  let timer: number | undefined;

  const schedule = () => {
    if (stopped) return;
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(run, document.visibilityState === "hidden" ? hiddenMs : activeMs);
  };
  const run = async () => {
    if (stopped || running) return;
    running = true;
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
    try {
      await poll();
    } catch {
      // A later poll or focus event retries transient network failures.
    } finally {
      lastRunAt = Date.now();
      running = false;
      schedule();
    }
  };
  const wake = () => {
    if (document.visibilityState === "hidden") return;
    // Returning to the tab fires both `visibilitychange` and `focus`, and
    // clicking back into the document fires `focus` on its own. One fresh
    // fetch is plenty — recently refreshed data is served as-is.
    if (Date.now() - lastRunAt < 5_000) return;
    void run();
  };

  window.addEventListener("focus", wake);
  document.addEventListener("visibilitychange", wake);
  if (options.runImmediately ?? true) void run();
  else schedule();

  return () => {
    stopped = true;
    if (timer !== undefined) window.clearTimeout(timer);
    window.removeEventListener("focus", wake);
    document.removeEventListener("visibilitychange", wake);
  };
}
